import { PlusOutlined, QrcodeOutlined, UnlockFilled } from "@ant-design/icons";
import { Button, message, Popconfirm, Space, Tag, Tooltip } from "antd";
import _ from "lodash";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../api";
import apiPaths from "../../api/paths";
import { useHasEditPermission } from "../../common/hooks";
import DynamicTable from "../../components/DynamicTable";
import VehicleModal from "./components/VehicleModal";
import QRCode from "react-qr-code";
import { createFileName, useScreenshot } from "use-react-screenshot";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import VehicleMarkers from "./components/VehicleMarkers";
import "leaflet/dist/leaflet.css";

const DEFAULT_COLUMNS = {
  vehicle_number: {
    show: true,
    label: "Vehicle Numbere",
    key: "vehicle_number",
  },
  imei_number: {
    show: true,
    label: "IMEI Number",
    key: "imei_number",
  },
  battery_voltage: {
    show: true,
    label: "Voltage",
    key: "battery_voltage",
  },
  type: {
    show: false,
    label: "Type",
    key: "type",
  },
  price_per_min: {
    show: false,
    label: "Price / Min",
    key: "price_per_min",
  },
  location: {
    show: true,
    label: "Current Location",
    key: "location",
  },
  mfg_date: {
    show: false,
    label: "MFG Dat",
    key: "mfg_date",
  },

  dock_station: {
    show: true,
    label: "Dock Station",
    key: "dock_station",
  },
  updated_by: {
    show: false,
    label: "Updated By",
    key: "updated_by",
  },
  created_by: {
    show: false,
    label: "Created By",
    key: "created_by",
  },
  lock_status: {
    show: true,
    label: "Lock Status",
    key: "lock_status",
  },
};

function Vehicles() {
  const [editingKey, setEditingKey] = useState(null);
  const [vehicles, setVehicles] = useState({});
  const [stations, setStations] = useState([]);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const allowEdit = useHasEditPermission();
  const navigate = useNavigate();
  const [qrCodeData, setQrCodeData] = useState(null);
  const ref = useRef(null);
  const [image, takeScreenshot] = useScreenshot();
  const [activeVehicle, setActiveVehicle] = useState(null);
  const [showMap, setShowMap] = useState(false);

  const download = (image, { name = "img", extension = "png" } = {}) => {
    const a = document.createElement("a");
    a.href = image;
    a.download = createFileName(extension, name);
    a.click();
  };

  const getImage = (id) => {
    return takeScreenshot(ref.current).then((img) =>
      download(img, { name: "cycle_" + id, extension: "png" })
    );
  };

  const handleGenerateQRCode = (data) => {
    setQrCodeData(data.vehicle_number);
    setTimeout(() => {
      getImage(data.vehicle_number);
    }, 1000);
  };

  useEffect(() => {
    fetchVehicles();
    fetchStations();
  }, []);

  const fetchStations = async () => {
    const res = await apiRequest({
      path: apiPaths.stations,
    });
    if (!res.error) {
      setStations(res.results);
    }
  };

  const fetchVehicles = async () => {
    const res = await apiRequest({
      path: apiPaths.vehicles,
    });
    if (!res.error) {
      let vehiclesObj = res.results.reduce((vehicles, vehicle) => {
        vehicles[vehicle._id] = vehicle;
        return vehicles;
      }, {});
      setVehicles(vehiclesObj);
    } else {
      message.error(res.error_description);
    }
  };

  const handleVehicleLock = async (vehicleId, status) => {
    const res = await apiRequest({
      path: apiPaths.vehicles + `/${vehicles[vehicleId].vehicle_number}/lock`,
      method: "POST",
      data: {
        lock: status,
      },
    });
    if (!res.error) {
      let updatedVehicles = { ...vehicles };
      updatedVehicles[vehicleId].lock_status = status;
      setVehicles(updatedVehicles);
      return;
    }
    message.error(res.error_description);
  };

  const updateAllVehiclesLockStatus = async (status) => {
    const res = await apiRequest({
      path: apiPaths.vehicles + `/locks`,
      method: "POST",
      data: {
        lock: status,
      },
    });
    if (!res.error) {
      let updatedVehicles = { ...vehicles };
      res.results.forEach((vehicle) => {
        updatedVehicles[vehicle._id] = {
          ...updatedVehicles[vehicle._id],
          ...vehicle,
        };
      });
      setVehicles(updatedVehicles);
      return;
    }
    message.error(res.error_description);
  };

  const COLUMNS = [
    {
      title: "Vehicle Number",
      dataIndex: "vehicle_number",
      fixed: "left",
      width: 150,
      filterType: "search",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <a
            onClick={() => {
              if (showMap) {
                if (record.location?.coordinates) setActiveVehicle(record);
              } else {
                navigate(`/vehicles/${record.vehicle_number}`, {
                  state: {
                    id: record._id,
                  },
                });
              }
            }}
          >
            <Highlighter
              highlightStyle={{
                backgroundColor: "#ffc069",
                padding: 0,
              }}
              searchWords={[]}
              autoEscape
              textToHighlight={text ? text.toString() : ""}
            />
          </a>
          <Tooltip title="download QR code">
            <Button
              onClick={() => handleGenerateQRCode(record)}
              type="link"
              style={{ marginLeft: 10 }}
            >
              <QrcodeOutlined style={{ fontSize: 16 }} />
            </Button>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "IMEI Number",
      dataIndex: "imei_number",
      width: 170,
      filterType: "search",
    },
    {
      title: "Voltage",
      dataIndex: "battery_voltage",
      width: 90,
      sorter: (a, b) => a.battery_voltage - b.battery_voltage,
      render: (value, record) => {
        return (
          <div>
            <div style={{ color: value < 39 ? "red" : "green" }}>
              {value + " V"}
            </div>
            <div style={{ fontSize: 10 }}>
              {record.timestamp
                ? moment(record.timestamp.replace("Z", "")).fromNow()
                : "Un Known"}
            </div>
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      width: 70,
    },
    {
      title: "Price / Min",
      dataIndex: "price_per_min",
      width: 100,
    },
    {
      title: "Current Location",
      dataIndex: "location",
      width: "220px",
      render: (_, record) => {
        return record.location?.coordinates.join(", ");
      },
    },
    {
      title: "MFG Date",
      dataIndex: "mfg_date",
      width: "150px",
      render: (_, record) => {
        return record.mfg_date
          ? moment(record.mfg_date).format("DD-MM-YYYY HH:mm")
          : "Not Given";
      },
    },
    {
      title: "Dock Station",
      dataIndex: "dock_station.name",
      filterType: "search",
      width: "150px",
      render: (_, record) => {
        return (
          <div>
            <div>{record.dock_station?.name || "NA"}</div>
          </div>
        );
      },
    },
    {
      title: "Created By",
      dataIndex: "created_by",
      width: "150px",
      render: (_, record) => {
        return (
          <div>
            <div>{record.created_by?.fullname}</div>
            <div style={{ color: "#a7a7a7", fontSize: 11 }}>
              {moment(record.created_at).fromNow()}
            </div>
          </div>
        );
      },
    },

    {
      title: "Updated By",
      dataIndex: "updated_by",
      width: "150px",
      render: (_, record) => {
        return (
          <div>
            <div>
              {record.updated_by
                ? record.updated_by?.fullname
                : record.created_by?.fullname}
            </div>
            <div style={{ color: "#a7a7a7", fontSize: 11 }}>
              {moment(record.updated_at).fromNow()}
            </div>
          </div>
        );
      },
    },
    {
      title: "Lock Status",
      dataIndex: "lock_status",
      fixed: "right",
      width: 150,
      filters: [
        {
          text: "Locked",
          value: true,
        },
        {
          text: "Un Locked",
          value: false,
        },
      ],
      onFilter: (value, record) => record.lock_status == value,
      render: (_, record) => {
        return (
          <>
            <div>
              <Tag color={record.lock_status ? "red" : "green"}>
                {record.lock_status ? "Locked" : "Un Locked"}
              </Tag>
              {allowEdit ? (
                <div style={{ marginTop: 5 }}>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => {
                      handleVehicleLock(record._id, !record.lock_status);
                    }}
                  >
                    {`${record.lock_status ? "Un Lock" : "Lock"} It`}
                  </Button>
                </div>
              ) : null}
            </div>
          </>
        );
      },
    },
    {
      title: "Last Health Status",
      dataIndex: "last_status",
      fixed: "right",
      width: 220,
      filters: [
        {
          text: "Available",
          value: "Available",
        },
        {
          text: "Service Done",
          value: "Service Done",
        },
        {
          text: "Service Required",
          value: "Service Required",
        },
        {
          text: "In Service",
          value: "In Service",
        },
        {
          text: "Battery Low",
          value: "Battery Low",
        },
      ],
      onFilter: (value, record) => {
        if (value == "Battery Low") {
          return record.battery_voltage < 39;
        }
        return record.status == value;
      },
      render: (_, record) => {
        return (
          <>
            <div>
              <Tag color={record.status == "Available" ? "green" : "orange"}>
                {record.status}
              </Tag>
            </div>
            <div>{record.last_status?.remark}</div>
          </>
        );
      },
    },
  ];

  const handleRowCanceled = () => {
    setShowVehicleModal(false);
  };

  const handleRowEdit = (idx) => {
    setEditingKey(idx);
    setShowVehicleModal(true);
  };

  const handleRowSave = async (row) => {
    if (editingKey == null) {
      const res = await apiRequest({
        path: apiPaths.vehicles,
        method: "POST",
        data: row,
      });
      if (res.error) {
        message.error(res.error_description);
        return;
      }
      vehicles[res._id] = res;
      setVehicles({
        [res._id]: res,
        ...vehicles,
      });
      setShowVehicleModal(false);
      return;
    }
    let key = editingKey;
    const res = await apiRequest({
      path: apiPaths.vehicles + `/${key}`,
      method: "PUT",
      data: row,
    });
    if (res.error) {
      message.error(res.error_description);
      return;
    }
    vehicles[editingKey] = res;
    setVehicles({ ...vehicles });
    setEditingKey(null);
    setShowVehicleModal(false);
  };

  return (
    <div style={{ height: "80vh" }}>
      {allowEdit ? (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginRight: 10,
          }}
        >
          <Space>
            <Button onClick={() => setShowMap(!showMap)}>
              {showMap ? "Hide Map" : "Show Map"}
            </Button>
            <Popconfirm
              title="Are you sure to Lock all Active Vehicles."
              onConfirm={() => {
                updateAllVehiclesLockStatus(true);
              }}
              okText="Yes"
              cancelText="Not Now"
            >
              <Button
                type="primary"
                style={{
                  backgroundColor: "orange",
                  border: 0,
                }}
              >
                Lock All Vehicles
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Are you sure to Un Lock all Active Vehicles."
              onConfirm={() => {
                updateAllVehiclesLockStatus(false);
              }}
              okText="Yes"
              cancelText="Not Now"
            >
              <Button
                type="primary"
                style={{
                  backgroundColor: "green",
                  border: 0,
                }}
              >
                Un Lock All Vehicles
              </Button>
            </Popconfirm>
            <Tooltip title="Add Vehicle">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingKey(null);
                  setShowVehicleModal(true);
                }}
              />
            </Tooltip>
          </Space>
        </div>
      ) : null}
      <div style={{ width: "100%", display: "flex", marginTop: 10 }}>
        <div
          style={{
            height: "100%",
            width: showMap ? "60%" : "100%",
            marginTop: 30,
          }}
        >
          <DynamicTable
            dataSource={Object.values(vehicles)}
            columns={COLUMNS}
            defaultColumns={DEFAULT_COLUMNS}
            pagination={false}
            requireEdit={allowEdit}
            scroll={{ y: "65vh" }}
            style={{ marginTop: 0 }}
            editHandler={(record) => handleRowEdit(record._id)}
          />
        </div>
        {showMap ? (
          <div style={{ width: "40%", height: "77vh" }}>
            <MapContainer
              center={[21.1667, 77.833298]}
              zoom={5}
              style={{ width: "100%", height: "100%" }}
              // scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {/* {renderVehicles(vehicles)} */}
              <VehicleMarkers
                activeVehicle={activeVehicle}
                vehicles={Object.values(vehicles)}
              />
            </MapContainer>
          </div>
        ) : null}
      </div>
      {qrCodeData ? (
        <div
          style={{
            position: "absolute",
            right: -300,
            backgroundColor: "#fff",
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            width: 200,
            height: 230,
          }}
          ref={(_ref) => (ref.current = _ref)}
        >
          <div style={{ marginBottom: 10, fontSize: 18, fontWeight: "bold" }}>
            Scan to Unlock <UnlockFilled />
          </div>
          <div
            style={{
              height: 170,
              width: 170,
            }}
          >
            <QRCode
              style={{ width: "100%", height: "100%" }}
              value={qrCodeData}
            />
          </div>
        </div>
      ) : null}
      {showVehicleModal ? (
        <VehicleModal
          open={showVehicleModal}
          onSave={handleRowSave}
          onCancel={handleRowCanceled}
          data={vehicles[editingKey]}
          isUpdate={editingKey}
          stations={stations}
        />
      ) : null}
    </div>
  );
}

export default Vehicles;
